import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { OrderProduct, LoadingData, Duration } from 'common/types';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { usePermissions } from 'hooks/usePermissions';
import { DEFAULT_START_TIME_SLOTS, getLocationOpeningHours } from './utils';
import {
	getEndDateWithInventoryBlockers,
	getStartDateWithInventoryBlockers,
} from 'common/utils/productUtils';
import {
	addAvailabilityToTimeSlots,
	getTimeSlotsWithoutAvailabilities,
} from 'common/modules/availabilities/timeSlots';
import { notNull } from 'common/utils/common';
import { mergeTimeSlots } from 'common/modules/availabilities/utils';
import {
	ProductTotalAvailability,
	getOverbookedVariantIdsFromProductTotalAvailabilitiesWithTimes,
	getSelectedVariantAmountsWithTimes,
} from 'common/api/frontend/inventory_new';
import {
	AvailabilityByTime,
	TimeSlotType,
	ValidSlotsByTime,
} from 'common/modules/availabilities/timeSlots/types';
import { getUniqueProducts } from 'common/utils/rentalUtils';
import ConfirmationBanner from './ConfirmationBanner';
import DaySelectorCalendar from './DaySelectorCalendar';
import TimeSlotList from './TimeSlotList';
import {
	dateIsClosed,
	dateIsOutsideOpeningHours,
	getNewEndTime,
} from 'common/utils/dateCalculations';
import { TimeSelectorArgs, TimeSelectorStyling } from 'components/TimeSelector/types';
import OverbookedBanner from 'components/OverbookedBanner';
import { handleQuantityAndAvailabilityFetch } from 'common/api/frontend/inventory_new/getQuantitiesAndAvailabilities';
import { useHasFeature } from 'hooks/useHasFeature';

interface Props {
	selectorType: TimeSelectorArgs;
	date: string;
	onDateChange: (value: moment.Moment) => void;
	onClose: () => void;
	products: OrderProduct[];
	startLocationId: string;
	endLocationId: string;
	duration: Duration;
	minDate?: moment.Moment;
	label?: string;
	ignoredProductIds?: string[];
	styling?: TimeSelectorStyling;
}

const TimeSelector = (props: Props) => {
	const classes = useStyles();
	const {
		date,
		onDateChange,
		selectorType,
		onClose,
		products,
		startLocationId,
		endLocationId,
		label,
		duration,
		minDate,
		ignoredProductIds,
		styling,
	} = props;
	const { durationInSeconds } = duration;
	const { hasFeature } = useHasFeature();
	const [currentDate, setCurrentDate] = useState<moment.Moment>(moment(date));
	const [showTimesOutsideHours, setShowTimesOutsideHours] = useState<boolean>(false);
	const [productAvailabilities, setProductAvailabilities] = useState<
		LoadingData<ProductTotalAvailability[] | null>
	>({ data: null, loading: false, error: null });

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const stockProductsMap = useSelector(StockSelectors.stockProductsById);
	const shopMainLocationId = useSelector(ShopSelectors.activeShopVisitingAddress)!.id;
	const _shopOpeningHours = useSelector(ShopSelectors.activeShopOpeningHoursWithExceptions);
	const shopTimeZone = useSelector(ShopSelectors.getShopTimeZone);

	const dateChangedSelectorClosed = useRef<boolean>(false);

	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');

	const currentMonthStart = moment(currentDate).startOf('month').toISOString();
	const currentMonthEnd = moment(currentDate).endOf('month').toISOString();
	const isSameDate = currentDate.isSame(date);
	const availabilitiesLoading = productAvailabilities.loading;
	const hasProducts = products.length > 0;
	const useAvailabilities = !hasFeature('DISABLE_PRODUCT_AVAILABILITY_LOADING', { scope: 'shop' });

	const isEndTime = selectorType.type === 'endTime';

	const variantAmountsWithTimes = useMemo(() => getSelectedVariantAmountsWithTimes(products), [
		products,
	]);

	const currentOpeningHours = useMemo(
		() =>
			getLocationOpeningHours({
				shopOpeningHours: _shopOpeningHours,
				locationId: isEndTime ? endLocationId : startLocationId,
				mainLocationId: shopMainLocationId,
			}),
		[endLocationId, shopMainLocationId, _shopOpeningHours, startLocationId, isEndTime],
	);

	const from = selectorType.type === 'endTime' ? selectorType.startTime : currentDate;
	const to =
		selectorType.type === 'endTime'
			? moment(currentDate)
			: moment(getNewEndTime(from.toISOString(), duration, currentOpeningHours, shopTimeZone));
	const currentDateIsClosed = dateIsClosed(currentDate, currentOpeningHours);
	const startTimeIsOutsideOpeningHours = dateIsOutsideOpeningHours(from, currentOpeningHours);
	const endTimeIsOutsideOpeningHours = dateIsOutsideOpeningHours(to, currentOpeningHours);
	const isOutsideOpeningHours =
		startTimeIsOutsideOpeningHours || endTimeIsOutsideOpeningHours || currentDateIsClosed;

	const overbookedVariantIds =
		(productAvailabilities.data &&
			getOverbookedVariantIdsFromProductTotalAvailabilitiesWithTimes(
				productAvailabilities.data,
				products,
				{
					startDate: from.toISOString(),
					endDate: to.toISOString(),
				},
			)) ??
		[];

	const hasOverbookedVariants = !!overbookedVariantIds.length;

	const showOverbookedBanner = !isSameDate && (isOutsideOpeningHours || hasOverbookedVariants);
	const confirmationBannerDisabled =
		!!isSameDate ||
		(!allowOverbookings && hasOverbookedVariants) ||
		(!allowOverbookings && isOutsideOpeningHours && !!styling?.disableSaveOfInvalidDateSelection);

	const getMinCalendarDate = useCallback((): moment.Moment => {
		const startDayUnix = selectorType.type === 'endTime' ? selectorType.startTime.valueOf() : 0;
		const minDateUnix = minDate && moment(minDate).valueOf();

		return minDateUnix ? moment(Math.max(minDateUnix, startDayUnix)) : moment(startDayUnix);
	}, [minDate, selectorType]); // Using object here as it has been memoized in parent component already.

	const timeSlotsWithoutAvailabilities = useMemo(() => {
		const minCalendarDate = getMinCalendarDate();
		const startDate =
			isEndTime || minCalendarDate.isAfter(currentMonthStart)
				? minCalendarDate
				: moment(currentMonthStart);
		return getTimeSlotsWithoutAvailabilities({
			start: startDate,
			end: moment(currentMonthEnd),
			openingHours: currentOpeningHours,
			variant: 'all_times',
			startTimeSlots: DEFAULT_START_TIME_SLOTS,
			useBookingBuffer: undefined,
			timeSlotType: isEndTime ? 'endTime' : 'startTime',
		});
	}, [currentMonthEnd, currentMonthStart, getMinCalendarDate, currentOpeningHours, isEndTime]);

	const timeSlotsWithAvailabilities: AvailabilityByTime | undefined = useMemo(() => {
		if (
			!variantAmountsWithTimes.length ||
			!productAvailabilities.data ||
			productAvailabilities.loading ||
			dateChangedSelectorClosed.current
		) {
			return undefined;
		}
		const availabilities = variantAmountsWithTimes
			.map(({ id: variantId, productId, duration, amount }) => {
				const availability = productAvailabilities.data?.find(
					(availability) => availability.availabilityRangesByVariant[variantId],
				);
				if (!availability) return null;
				const stockProduct = stockProductsMap[productId];
				const timeSlotType: TimeSlotType =
					selectorType.type === 'endTime'
						? {
								type: selectorType.type,
								startTime: selectorType.startTime,
						  }
						: {
								type: selectorType.type,
								selectedDuration: duration,
						  };
				const timeSlotAvailabilities = addAvailabilityToTimeSlots({
					product: stockProduct,
					stockProducts: stockProducts,
					timeSlotType,
					timeSlots: timeSlotsWithoutAvailabilities,
					availabilities: availability.availabilityRangesByVariant[variantId],
					startTimeCounts: availability.startTimeCountsByVariant[variantId],
					openingHours: currentOpeningHours,
					alreadySelected: amount ?? 0,
					startTimeSlots: DEFAULT_START_TIME_SLOTS,
				});
				return timeSlotAvailabilities;
			})
			.filter(notNull);
		return mergeTimeSlots(availabilities);
	}, [
		variantAmountsWithTimes,
		productAvailabilities.data,
		productAvailabilities.loading,
		stockProducts,
		stockProductsMap,
		selectorType,
		timeSlotsWithoutAvailabilities,
		currentOpeningHours,
	]);

	const timeSlots: AvailabilityByTime | ValidSlotsByTime =
		timeSlotsWithAvailabilities ?? timeSlotsWithoutAvailabilities;

	useEffect(() => {
		const isOutsideOpeningHours = dateIsOutsideOpeningHours(moment(date), currentOpeningHours);
		if (allowOverbookings && isOutsideOpeningHours) {
			setShowTimesOutsideHours(true);
		}
	}, [date, currentOpeningHours, allowOverbookings]);

	useEffect(() => {
		const fetchAvailabilities = async (
			start: moment.Moment,
			end: moment.Moment,
			products: OrderProduct[],
			ignoredOrderProductIds?: string[],
		) => {
			setProductAvailabilities({ data: null, error: null, loading: true });
			const loadProductAvailabilities = await Promise.all(
				getUniqueProducts(products, stockProductsMap).map((product) => {
					const startDateWithInventoryBlockers = getStartDateWithInventoryBlockers({
						startDate: start.toISOString(),
					});
					const endDateWithInventoryBlockers = getEndDateWithInventoryBlockers(
						end.toISOString(),
						product,
						stockProducts,
					);
					return handleQuantityAndAvailabilityFetch({
						product,
						stockProducts,
						startDate: startDateWithInventoryBlockers,
						endDate: endDateWithInventoryBlockers,
						shopId,
						startLocationId,
						shopLocations,
						ignoredOrderProductIds,
						purchaseType: 'rental',
					});
				}),
			);
			setProductAvailabilities({ data: loadProductAvailabilities, error: null, loading: false });
		};
		if (!hasProducts || dateChangedSelectorClosed.current || !useAvailabilities) return;
		const minDate = getMinCalendarDate();

		const startDate =
			isEndTime || minDate.isAfter(currentMonthStart) ? moment(minDate) : moment(currentMonthStart);
		const endDate = moment(currentMonthEnd).add(durationInSeconds, 'seconds');
		fetchAvailabilities(startDate, endDate, products, ignoredProductIds);
	}, [
		dateChangedSelectorClosed,
		currentMonthEnd,
		currentMonthStart,
		durationInSeconds,
		hasProducts,
		products,
		shopId,
		shopLocations,
		startLocationId,
		stockProducts,
		stockProductsMap,
		ignoredProductIds,
		isEndTime,
		getMinCalendarDate,
		useAvailabilities,
	]);

	const handleTimeSlotDateSave = (changedDate: moment.Moment) => {
		dateChangedSelectorClosed.current = true;
		onDateChange(changedDate);
		onClose();
	};

	const handleCurrentDateChange = (date: moment.Moment) => {
		const monthChange = !currentDate.isSame(date, 'month');
		setCurrentDate(date);
		if (monthChange) {
			setProductAvailabilities({ data: null, loading: false, error: null });
		}
	};

	return (
		<>
			<Box className={classes.topBanner}>
				<ConfirmationBanner
					onCancel={onClose}
					onSave={() => handleTimeSlotDateSave(currentDate)}
					disabled={confirmationBannerDisabled}
					isEndTime={isEndTime}
					products={products}
					overbookedBanner={
						!isSmallScreen && showOverbookedBanner
							? {
									overbookedVariantIds: overbookedVariantIds,
									isBeforeOpeningHours: startTimeIsOutsideOpeningHours,
									isOutsideOpeningHours: isOutsideOpeningHours,
							  }
							: undefined
					}
				/>
				{!!showOverbookedBanner && (
					<OverbookedBanner
						allowOverbookings={allowOverbookings}
						rentalProducts={products}
						overbookedVariantIds={overbookedVariantIds ?? []}
						openingHours={{
							isBeforeOpeningHours: startTimeIsOutsideOpeningHours,
							isOutsideOpeningHours: isOutsideOpeningHours,
							isClosed: currentDateIsClosed,
						}}
						styling={{ sticky: true }}
					/>
				)}
			</Box>
			<Box className={classes.wrapper} id="start-time-selector">
				<Box className={classes.calendarWrapper}>
					<Box className={classes.calendarTitle}>
						<Typography variant="body1">{label}</Typography>
					</Box>
					<DaySelectorCalendar
						existingDateSelection={currentDate.toISOString()}
						setDay={handleCurrentDateChange}
						selectorType={selectorType}
						duration={duration}
						openingHours={currentOpeningHours}
						timeSlots={timeSlots}
						loading={availabilitiesLoading}
						minDate={minDate}
					/>
				</Box>
				<Box className={classes.listWrapper}>
					<TimeSlotList
						duration={duration}
						hasProducts={hasProducts}
						showAvailability={useAvailabilities}
						openingHours={currentOpeningHours}
						showTimesOutsideOpeningHours={showTimesOutsideHours}
						timeSlots={timeSlots}
						loading={availabilitiesLoading}
						selectorType={selectorType}
						existingDateSelection={currentDate.toISOString()}
						onToggleShowTimesOutsideOpeningHours={() =>
							setShowTimesOutsideHours(!showTimesOutsideHours)
						}
						onSelectDate={handleCurrentDateChange}
						styling={styling}
						minDate={minDate}
						overbookedBanner={!!showOverbookedBanner}
					/>
				</Box>
			</Box>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		wrapper: {
			[theme.breakpoints.up('md')]: {
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'stretch',
				maxHeight: '500px',
			},
			position: 'relative',
		},
		closeButton: {
			position: 'absolute',
			top: 5,
			left: 5,
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			zIndex: 1000,
		},
		calendarWrapper: {
			flex: 1,
			padding: theme.spacing(4),
			paddingTop: theme.spacing(2),
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			background: theme.palette.background.default,
			position: 'relative',
		},
		calendarTitle: {
			padding: theme.spacing(2),
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
		},
		listWrapper: {
			flex: 1,
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'stretch',
			background: theme.palette.background.paper,
		},
		headerText: {
			marginLeft: theme.spacing(5),
		},
		topBanner: {
			borderBottom: `1px solid ${theme.palette.border.paper}`,
			zIndex: 1100,
			[theme.breakpoints.down('lg')]: {
				position: 'sticky',
				top: 0,
			},
		},
	}),
);

export default TimeSelector;
